import React, { ReactNode, FC, useContext, useMemo, ReactElement } from 'react';

import FlexCol, { FlexColProps } from '../../structure/#FlexCol';
import FormColContext from '../#FormColContext';
import FieldGroupContext from '../#FieldGroupContext';
import Row from '../../grid/Row';
import Col from '../../grid/Col';
import ValidationMessage from '../ValidationMessage';
import { StyledLabel, StyledTip, StyledWrapper } from './FormCol.styles';

export interface FormColProps extends Omit<FlexColProps, 'ColContext'> {
  /** Label of the col. */
  label?: ReactNode;
  /** Pass a tooltip instance to display a tip next ot the label. */
  tip?: ReactNode;
  /** Font weight of the label. */
  boldLabel?: boolean;
  /** Margin bottom of the label */
  labelMarginBottom?: number;
}

interface FormColInternalProps {
  formColId: number;
}

interface FormColComposedProps extends FormColProps, FormColInternalProps {

}

const FormColDefaultProps = {
  labelMarginBottom: 24,
  boldLabel: true,
};

const FormCol: FC<FormColComposedProps> = ({ formColId, label, children, tip, labelMarginBottom, boldLabel, ...props }) => {
  const colId = useMemo(() => `formColLabel${formColId}`, [formColId]);
  const { gutterWidth } = useContext(FormColContext);
  const { grouped } = useContext(FieldGroupContext) || {};

  const childrenArray = useMemo(() => React.Children.toArray(children), [children]);

  const filteredChildrenArray = useMemo(() => childrenArray.filter((child) => (
    (child as ReactElement).type !== ValidationMessage
  )), [childrenArray]);

  const filteredChildrenCount = useMemo(() => filteredChildrenArray.length, [filteredChildrenArray]);
  const isMultipleChildren = useMemo(() => filteredChildrenCount > 1, [filteredChildrenCount]);

  return (
    <FlexCol
      ColContext={FormColContext}
      colId={colId}
      label={label}
      {...props}
    >
      <StyledWrapper data-testid="FormColLabel" labelMarginBottom={labelMarginBottom}>
        {(label || tip) && (
          <StyledLabel id={colId} grouped={grouped} boldLabel={boldLabel}>
            {label}
            {tip && <StyledTip>{tip}</StyledTip>}
          </StyledLabel>
        )}
        {
          (isMultipleChildren)
            ? (
              <Row gutterWidth={gutterWidth}>
                {React.Children.map(children, (child, index) => {
                  if ((child as ReactElement).type === ValidationMessage) {
                    return child;
                  }

                  return (
                    <Col key={index}>{child}</Col>
                  );
                })}
              </Row>
            )
            : children
        }
      </StyledWrapper>
    </FlexCol>
  );
};

FormCol.defaultProps = FormColDefaultProps;

export default FormCol;
